<template>
  <v-container fluid class="company">
    <v-toolbar
      flat
      class="toolbar--white action-bar overflow--y"
      :class="{ 'action-bar--ml': !mainMenuIsOpen }"
    >
      <Breadcrumb :routes="breadcrumbItems" />

      <v-layout v-if="isSuccess" align-center row>
        <v-spacer />

        <CompanyMenuActions :company="company || { id: routeId }" />

        <v-btn
          v-if="claims.company['ROLE_PUT_COMPANY_ID_DISABLE']"
          class="action-bar__btn"
          round
          depressed
          color="accent"
          @click.prevent="changeCompanyStatusDialog = true"
        >
          {{ $t(`${namespace}.changeStatus`) }}
        </v-btn>

        <StatusBullet
          v-if="isAdmin"
          class="mr-3 ml-3"
          :status="companyStatus"
          bulletPosition="right"
        />
      </v-layout>
    </v-toolbar>

    <div class="page-content">

      <!-- loading -->
      <div class="position--relative">
        <transition name="fade">
          <v-progress-linear
            v-if="isLoading"
            class="progress-linear--absolute"
            color="warning"
            height="3"
            indeterminate
          />
        </transition>
      </div>

      <div v-if="isIndebted.alert" class="alert">
        <v-icon color="error">warning</v-icon> {{ isIndebted.message }}
      </div>

      <!-- error -->
      <div v-if="isFailed" class="text-xs-center mt-5">
        <span>
          {{ $t('Common.genericErrorMsg') }}<br>
          <a
            href=""
            :title="$t('Common.tryAgain')"
            class="text--link accent--text"
            @click.prevent="getCompanyData"
          >
            {{ $t('Common.tryAgain') }}.
          </a>
        </span>
      </div>

      <!-- success -->
      <div v-if="isSuccess" class="company__wrapper">
        <header class="page-header">
          <h1 class="page-title">{{ companyData.displayName }}</h1>
          <p v-if="companyData.name" class="page-desc">{{ companyData.name }}</p>
        </header>

        <div class="page-body">
          <CompanyDetails :company="companyData" />
        </div>
      </div>
    </div>

    <ChangeCompanyStatusDialog
      :visible="changeCompanyStatusDialog"
      @close="(reload) => {
        changeCompanyStatusDialog = false
        if (reload) getCompanyData()
      }"
    />

    <AllowEmployeeAccessDialog
      :visible="allowEmployeeAccessDialog"
      @close="allowEmployeeAccessDialog = false"
    />
  </v-container>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { hasRole, isObject } from 'utils/functions'
import Breadcrumb from 'components/Breadcrumb'
import CompanyDetails from 'components/company/CompanyDetails'
import ChangeCompanyStatusDialog from 'components/company/ChangeCompanyStatusDialog'
import AllowEmployeeAccessDialog from 'components/company/AllowEmployeeAccessDialog'
import StatusBullet from 'components/company/StatusBullet'
import CompanyMenuActions from 'components/company/companies/CompanyMenuActions'

export default {
  components: {
    Breadcrumb,
    CompanyDetails,
    ChangeCompanyStatusDialog,
    AllowEmployeeAccessDialog,
    StatusBullet,
    CompanyMenuActions
  },
  computed: {
    ...mapState({
      isAdmin: state => {
        return state.session.user.business.isAdministrator ||
          state.userClaims.claims.profiles['ROLE_ADMINISTRATOR'] ||
          state.userClaims.claims.profiles['ROLE_FINANCIAL_ADMIN']
      },
      company: state => state.company.selectedCompany,
      mainMenuIsOpen: state => state.mainMenu.isOpen,
      userPermission: state => state.session.user.permission,
      claims: state => state.userClaims.claims
    }),

    routeId () {
      if (this.$route.params.id) {
        return Number(this.$route.params.id)
      }

      if (this.company) {
        return this.company.id
      }

      return null
    },

    routeName () {
      return this.$route.name
    },

    companyStatus () {
      return this.isValidObject(this.companyData) && this.companyData.status
        ? this.companyData.status
        : null
    },

    isLoading () {
      return this.currentStatus === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.currentStatus === STATUS_SUCCESS
    },

    isFailed () {
      return this.currentStatus === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.CompanyDetailsPage',
      breadcrumbItems: [
        { title: this.$t('Common.settings') },
        { name: 'Company', title: this.$t('Views.CompanyPage.title') }
      ],
      currentStatus: null,
      companyData: null,
      changeCompanyStatusDialog: false,
      costCentersList: [],
      updateCostCenterList: false,
      projectList: [],
      updateProjectList: false,
      allowEmployeeAccessDialog: false,
      isIndebted: {
        alert: false,
        message: null
      }
    }
  },
  methods: {
    ...mapActions({
      successAlert: 'snackbarAlert/success',
      errorAlert: 'snackbarAlert/error',
      infoAlert: 'snackbarAlert/info'
    }),

    hasRole (group, role) {
      return hasRole(group, role)
    },

    isValidObject (val) {
      return isObject(val)
    },

    resetAlert () {
      this.isIndebted = {
        alert: false,
        message: null
      }
    },

    async getCompany () {
      this.companyData = null
      this.currentStatus = STATUS_IS_LOADING

      try {
        const { data: res } = await this.$apiService.getCompanyById(this.routeId)
        this.companyData = res.data
        this.redirectToHome(this.companyData)

        this.breadcrumbItems = [
          { title: this.$t('Common.settings') },
          { name: 'Company', title: this.$t('Views.CompanyPage.title') },
          { name: 'CompanyDetail', title: this.companyData.displayName }
        ]
        this.currentStatus = STATUS_SUCCESS
      } catch (err) {
        console.log('Error', err)
        if (err.response && err.response.status === 404) {
          this.$router.push({ name: 'Company' }).catch(() => {})
          this.errorAlert(this.$t(`${this.namespace}.companyNotFound`))
          return
        }

        this.errorAlert(this.$t('Messages.genericError'))
        this.currentStatus = STATUS_FAILED
      }
    },

    async getCompanyFinancialConfig () {
      try {
        this.resetAlert()

        const { data: res } = await this.$apiService.getCompanyFinancialConfig(this.routeId)

        if (this.companyData.status === 'indebt') {
          this.isIndebted = {
            alert: true,
            message: res.data.isIndebtedAppMessage
          }
        }
      } catch (err) {
        console.log('Error', err)
      }
    },

    async getCompanyData () {
      await this.getCompany()
      await this.getCompanyFinancialConfig()
    },

    redirectToHome ({ enabled = false, status = 'disabled' }) {
      if (!enabled && status === 'disabled') {
        this.$router.push({ name: 'Home' })
      }
    }
  },
  mounted () {
    this.getCompanyData()
  },
  watch: {
    company (to, from) {
      if (
        (to && this.routeName !== 'CompanyDetail') ||
        (
          to && to.id &&
          from && from.id &&
          Number(to.id) !== Number(from.id)
        ) ||
        (to && to.id && !from)
      ) {
        this.$router.push({
          name: 'CompanyDetail',
          params: { id: to.id }
        })

        return
      }

      if (!to) this.$router.push({ name: 'Company' })
    },

    $route (to, from) {
      if (parseInt(to.params.id) !== parseInt(from.params.id)) {
        this.getCompanyData()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.company__wrapper {
  max-width: 842px;
  margin: 0 auto;
}

.alert {
  background-color: #f2dede;
  color: #a94442;
  padding: 20px 18px;
  line-height: normal;
  text-align: center;

  .v-icon {
    display: inline-block;
    vertical-align: middle;
  }
}

.company {

  .page-header {
    padding: 23px 16px;
    position: relative;
    text-align: center;
  }

  .page-content {
    background-color: transparent;
  }
}
</style>
